import { Credentials } from './credentials';
import { operations, type OperationId, type Operations } from './generated/operations';
import { ApiProblem } from './problem';
import { Deadline } from './deadline';
import { Lapse, type Replay } from './lapse';
import { Replays } from './replays';

// A member may be left out when the operation declares none, or when every value inside it is optional
type Optional<Value> = undefined extends Value ? true : {} extends Value ? true : false;
type Member<Name extends string, Value> = Optional<Value> extends true ? { [Key in Name]?: Value } : { [Key in Name]: Value };

/**
 * What one call supplies: the path parameters, query and body the operation declares, and optionally the
 * idempotency key of the intended mutation.
 */
export type Request<Id extends OperationId> = Member<'params', Operations[Id]['params']> &
	Member<'query', Operations[Id]['query']> &
	Member<'body', Operations[Id]['body']> & {
		key?: string;
		signal?: AbortSignal;
		/** Whether it is sent again after a renewal, when not its operation's class (`Replays`); `null` never waits */
		replay?: Replay | null;
	};

interface Specs {
	origin: string;
	credentials: Credentials;
	fetch?: typeof fetch;
	/** Milliseconds one request may take, answer body included (`Deadline.DEFAULT` otherwise) */
	timeout?: number;
}

/**
 * Typed client of the management API, protocol beyond-cdn-management/1. Operations, paths and types are
 * generated from the contract; this class only performs the request the table describes.
 *
 * Every request has a deadline that covers reading its answer. A service that did not answer in time is
 * `UNAVAILABLE` with status 0, like one that could not be reached; for a change, the problem also says that
 * its outcome is unknown (`ApiProblem.unknown`), because the service may have made it.
 *
 * An answer that says the session ended waits on `lapse`, the page's `Session` of `@beyond-js/ui` (the family
 * rule on a session that ends while the person works): sent once more, under the same idempotency key, when the
 * session was renewed and its replay class allows it; otherwise rejected with a quiet problem (`ApiProblem.quiet`)
 * that no screen shows as an error.
 */
export class Api {
	#origin: string;
	#fetch: typeof fetch;
	#timeout: number;
	#refusals = new Set<(problem: ApiProblem) => void>();

	/** Where a request whose session ended waits for the page's `Session` */
	readonly lapse = new Lapse();

	#credentials: Credentials;
	get credentials() {
		return this.#credentials;
	}

	constructor({ origin, credentials, fetch: custom, timeout }: Specs) {
		this.#origin = origin;
		this.#credentials = credentials;
		this.#fetch = custom ?? ((input, init) => globalThis.fetch(input, init));
		this.#timeout = timeout ?? Deadline.DEFAULT;
	}

	/**
	 * Whether the service answers this page at all, after a request from it failed without an answer. A browser
	 * reports an address the service does not allow (CORS) exactly like a service that is down; a request that
	 * reads nothing (`no-cors`) gets through in the first case and not in the second, so the page can say which.
	 */
	async answers(): Promise<boolean> {
		// Bounded by its own timer as well: a transport that ignores the signal must not hold the page's answer
		let timer: ReturnType<typeof setTimeout> | undefined;
		const late = new Promise<boolean>(done => (timer = setTimeout(() => done(false), 3000)));
		const probe = this.#fetch(`${this.#origin}/healthz`, { mode: 'no-cors', credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(3000) }).then(
			() => true,
			() => false
		);
		return Promise.race([probe, late]).finally(() => clearTimeout(timer));
	}

	/**
	 * A new operation key. A model creates one per intended mutation and reuses it when it retries, so the
	 * service answers the original result instead of repeating the work.
	 */
	static key(): string {
		return `adm-${crypto.randomUUID()}`;
	}

	/**
	 * A new request identifier, sent as `x-request-id`. The service keeps it, answers it and logs under it, so the
	 * technical details of a failure can quote it even when no answer arrived.
	 */
	static trace(): string {
		return `adm-${crypto.randomUUID()}`;
	}

	/**
	 * Tells the listener about every `ADMISSION_REQUIRED` answer, whichever operation received it, so the
	 * holder of the caller's admission can read it again: an admission can be revoked while the page is open.
	 *
	 * @returns The function that ends the subscription
	 */
	refused(listener: (problem: ApiProblem) => void) {
		this.#refusals.add(listener);
		return () => void this.#refusals.delete(listener);
	}

	/**
	 * The address and the request one operation describes. The credential rules are the same for every call:
	 * a bearer token travels in the header, and the sentinel means the browser holds a cookie of the
	 * service's origin, which it sends itself and which must never be copied into a header.
	 */
	#request<Id extends OperationId>(id: Id, request: unknown, accept: string): { url: string; init: RequestInit; token?: string } {
		const descriptor = operations[id];
		const { params, query, body, key, signal } = (request ?? {}) as { params?: Record<string, string>; query?: Record<string, unknown>; body?: unknown; key?: string; signal?: AbortSignal };

		const path = descriptor.path.replace(/\{(\w+)\}/g, (_, name: string) => encodeURIComponent(params?.[name] ?? ''));
		const search = new URLSearchParams();
		for (const [name, value] of Object.entries(query ?? {})) value !== undefined && value !== '' && search.set(name, String(value));

		const headers: Record<string, string> = { accept, 'x-request-id': Api.trace() };
		const token = this.#credentials.token;
		const ambient = this.#credentials.ambient;
		if (token && !ambient) headers.authorization = `Bearer ${token}`;
		if (body !== undefined) headers['content-type'] = 'application/json';
		if (descriptor.idempotency === 'key') headers['idempotency-key'] = key ?? Api.key();

		const init: RequestInit = {
			method: descriptor.method,
			headers,
			body: body === undefined ? undefined : JSON.stringify(body),
			signal,
			credentials: ambient ? 'include' : 'omit'
		};
		return { url: `${this.#origin}${path}${search.size ? `?${search}` : ''}`, init, token };
	}

	/**
	 * Sends one request and reads its whole answer with `read`, under the deadline. A refusal `read` throws
	 * passes through; a caller that aborted gets its own abort back.
	 */
	async #send<Result>(url: string, init: RequestInit, read: (answer: Response) => Promise<Result>): Promise<Result> {
		const deadline = new Deadline(this.#timeout, init.signal);
		const request = (init.headers as Record<string, string> | undefined)?.['x-request-id'];
		try {
			return await deadline.run(async signal => read(await this.#fetch(url, { ...init, signal })));
		} catch (thrown) {
			if (thrown instanceof ApiProblem) throw thrown.traced(request);
			const changing = !['GET', 'HEAD'].includes(String(init.method ?? 'GET').toUpperCase());
			if (deadline.expired) throw ApiProblem.late(changing).traced(request);
			if (init.signal?.aborted) throw thrown;
			// A browser cannot tell a request that never left from one whose answer was lost on the way back, so a
			// change without an answer may have been made: its outcome is unknown, never "not sent"
			throw new ApiProblem('UNAVAILABLE', 'The request got no answer from the service', 0, changing ? { outcome: 'unknown' } : undefined).traced(request);
		}
	}

	/**
	 * Turns a refusal into the problem the caller throws, and tells the admission holder about the answer that
	 * concerns it. A session refusal of a credential other than the one now held is an answer of the past: it is
	 * thrown as it is and waits on nothing. A change answered by something other than the service is an unknown outcome.
	 */
	#refusal(status: number, document: unknown, token: string | undefined, init: RequestInit): ApiProblem {
		const problem = ApiProblem.from(status, document, !['GET', 'HEAD'].includes(String(init.method ?? 'GET').toUpperCase()));
		if (problem.ended && token !== this.#credentials.token) return problem.hush();
		if (problem.is('ADMISSION_REQUIRED')) for (const listener of [...this.#refusals]) listener(problem);
		return problem;
	}

	/**
	 * Sends a request, and once more after the session it was sent with was renewed, when its class allows it.
	 * A second refusal is not waited on again: the page's `Session` already answered this one.
	 */
	async #attempt<Result>(replay: Replay | null, send: () => Promise<Result>): Promise<Result> {
		try {
			return await send();
		} catch (thrown) {
			if (!(thrown instanceof ApiProblem) || !thrown.ended || thrown.quiet || replay === null || !this.lapse.present) throw thrown;
			if (!(await this.lapse.wait(thrown.reason, replay))) throw thrown.hush(replay === false ? this.lapse.unsent : undefined);
			try {
				return await send();
			} catch (again) {
				throw again instanceof ApiProblem && again.ended ? again.hush() : again;
			}
		}
	}

	/**
	 * @throws {ApiProblem} For every answer outside 2xx and when no answer arrives
	 */
	async call<Id extends OperationId>(id: Id, ...[request]: {} extends Request<Id> ? [Request<Id>?] : [Request<Id>]): Promise<Operations[Id]['response']> {
		const held = this.#keyed(id, request);
		return this.#attempt(Replays.of(id, held?.replay), () => {
			const { url, init, token } = this.#request(id, held, 'application/json');
			return this.#send(url, init, async answer => {
				if (answer.status === 204) return undefined as Operations[Id]['response'];
				const document = await answer.json().catch(() => undefined);
				if (answer.ok) return document as Operations[Id]['response'];
				throw this.#refusal(answer.status, document, token, init);
			});
		});
	}

	// The idempotency key is chosen once, so a request sent again after a renewal is the same intended change
	#keyed(id: OperationId, request: unknown): { key?: string; replay?: Replay | null } | undefined {
		const given = request as { key?: string; replay?: Replay | null } | undefined;
		if (operations[id].idempotency !== 'key' || given?.key) return given;
		return { ...given, key: Api.key() };
	}

	/**
	 * A request to a route of the service outside the management contract: the suite notification relay
	 * (`beyond-notifications/1` under `/v1/notifications`). The credential rules are the ones of every call.
	 *
	 * @param path The path under the API origin, with its query
	 * @throws {ApiProblem} For every answer outside 2xx and when no answer arrives
	 */
	async relay(method: 'GET' | 'POST', path: string, body?: unknown): Promise<unknown> {
		return this.#attempt(Replays.method(method), () => {
			const headers: Record<string, string> = { accept: 'application/json', 'x-request-id': Api.trace() };
			const token = this.#credentials.token;
			const ambient = this.#credentials.ambient;
			if (token && !ambient) headers.authorization = `Bearer ${token}`;
			if (body !== undefined) headers['content-type'] = 'application/json';
			const init: RequestInit = { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: ambient ? 'include' : 'omit' };

			return this.#send(`${this.#origin}${path}`, init, async answer => {
				const document = await answer.json().catch(() => undefined);
				if (answer.ok) return document;
				throw this.#refusal(answer.status, document, token, init);
			});
		});
	}

	/**
	 * Stored bytes of an operation that answers a file instead of a document.
	 *
	 * The service sends them as an attachment that nothing may run, so they are read here and never given to
	 * the browser as an address: a page that navigated to them would be asking this origin to open somebody
	 * else's output. What the caller receives is the bytes, their recorded media type and their size.
	 *
	 * @throws {ApiProblem} For every answer outside 2xx and when no answer arrives
	 */
	async retrieve<Id extends OperationId>(id: Id, ...[request]: {} extends Request<Id> ? [Request<Id>?] : [Request<Id>]): Promise<{ media: string; bytes: Uint8Array }> {
		return this.#attempt(Replays.of(id, (request as { replay?: Replay | null } | undefined)?.replay), () => {
			const { url, init, token } = this.#request(id, request, '*/*');
			return this.#send(url, init, async answer => {
				if (!answer.ok) throw this.#refusal(answer.status, await answer.json().catch(() => undefined), token, init);
				return { media: answer.headers.get('content-type') ?? 'application/octet-stream', bytes: new Uint8Array(await answer.arrayBuffer()) };
			});
		});
	}
}
